import { useEffect, useState } from 'react';
import { formatPhone, normalizePhone } from '@fsm/shared';
import { useCalls } from '../lib/calls';
import { useSoftphone } from '../lib/softphone';
import { Button, Toast } from './ui';

/**
 * The browser's own phone: a ringing call to answer, or a call in progress.
 *
 * Top centre, deliberately apart from the screen-pop in the bottom right. The
 * screen-pop answers "who is this and what do they want"; this answers "do I
 * pick up", and the two are read at different moments — this one first, with
 * a hand already moving to the mouse.
 *
 * Nothing here renders when the softphone is off, so a company with no number
 * sees the office exactly as it was.
 */
export function Softphone() {
  const phone = useSoftphone();
  const { liveCalls } = useCalls();

  /*
   * The name, when the screen-pop has already found one. Matched on the
   * parent call's sid first — the same call, unambiguously — and on the
   * number only when Twilio did not pass the sid through.
   */
  const ringingName = phone.incoming
    ? (liveCalls.find((c) => phone.incoming?.callSid && c.providerCallId === phone.incoming.callSid) ??
        liveCalls.find(
          (c) =>
            c.direction === 'inbound' &&
            normalizePhone(c.fromNumber) === normalizePhone(phone.incoming?.from ?? ''),
        ))?.customer?.name ?? null
    : null;

  return (
    <>
      {phone.incoming && !phone.active && (
        <div
          role="alertdialog"
          aria-label="Incoming call"
          className="fixed top-3 left-1/2 z-50 flex w-[min(460px,calc(100vw-32px))] -translate-x-1/2 items-center gap-3 rounded-card border border-accent bg-surface px-4 py-3 shadow-[0_24px_60px_-20px_rgb(23_43_34/0.45)]"
        >
          <span className="relative flex size-2.5 shrink-0">
            <span className="absolute inline-flex size-2.5 animate-ping rounded-full bg-accent opacity-75" />
            <span className="relative inline-flex size-2.5 rounded-full bg-accent" />
          </span>
          <div className="min-w-0 flex-1">
            <div className="text-[11.5px] font-semibold tracking-wide text-accent uppercase">
              Incoming call
            </div>
            <div className="truncate text-[15px] leading-snug font-semibold">
              {ringingName ?? formatPhone(phone.incoming.from)}
            </div>
            {ringingName && (
              <div className="truncate text-[12.5px] tnum text-ink-3">
                {formatPhone(phone.incoming.from)}
              </div>
            )}
          </div>
          <Button size="md" variant="danger" onClick={phone.decline}>
            Decline
          </Button>
          <Button size="md" variant="primary" icon="phone" onClick={phone.answer} autoFocus>
            Answer
          </Button>
        </div>
      )}

      {phone.active && (
        <div
          role="status"
          aria-label="Call in progress"
          className="fixed top-3 left-1/2 z-50 flex w-[min(460px,calc(100vw-32px))] -translate-x-1/2 items-center gap-3 rounded-card border border-line bg-surface px-4 py-3 shadow-[0_24px_60px_-20px_rgb(23_43_34/0.45)]"
        >
          <div className="min-w-0 flex-1">
            <div className="text-[11.5px] font-semibold tracking-wide text-ink-3 uppercase">
              {phone.active.connectedAt ? 'On the call' : 'Calling'}
            </div>
            <div className="truncate text-[15px] leading-snug font-semibold tnum">
              {formatPhone(phone.active.peer)}
              {phone.active.connectedAt && (
                <Elapsed since={phone.active.connectedAt} className="ml-2 font-normal text-ink-3" />
              )}
            </div>
          </div>
          <Button
            size="md"
            onClick={phone.toggleMute}
            aria-pressed={phone.active.muted}
            disabled={!phone.active.connectedAt}
          >
            {phone.active.muted ? 'Unmute' : 'Mute'}
          </Button>
          <Button size="md" variant="danger" icon="phone" onClick={phone.hangup}>
            Hang up
          </Button>
        </div>
      )}

      {phone.error && <Toast message={phone.error} tone="error" onDismiss={phone.clearError} />}
    </>
  );
}

/** m:ss since a moment, ticking. */
function Elapsed({ since, className }: { since: number; className?: string }) {
  const [now, setNow] = useState(() => Date.now());

  useEffect(() => {
    const t = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(t);
  }, []);

  const seconds = Math.max(0, Math.floor((now - since) / 1000));
  return (
    <span className={className}>
      {Math.floor(seconds / 60)}:{String(seconds % 60).padStart(2, '0')}
    </span>
  );
}
